import SEO from "@/components/base/SEO";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";

export default function Login() {
  const navigate = useNavigate();
  const { login, isLoading } = useAuth();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    setError("");

    if (!email || !password) {
      setError("Please fill in all fields");
      return;
    }

    const success = await login(email, password);
    if (success) {
      navigate("/home", { replace: true });
    } else {
      setError("Invalid credentials. Try the demo account below.");
    }
  };

  return (
    <>
      <SEO
        title="Sign In"
        description="Sign in to PayFlow AI to manage your payments, subscriptions, and reminders."
        noindex
      />
      <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-purple-50 flex flex-col">
        <div className="flex-1 flex flex-col justify-center px-5 max-w-[375px] mx-auto w-full">
          <div className="text-center mb-8">
            <h1
              className="text-3xl font-semibold text-slate-900"
              style={{ fontFamily: "'Pacifico', serif" }}
            >
              PayFlow AI
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Welcome back! Sign in to manage your payments.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="demo@payflow.ai"
                className="w-full h-12 bg-white rounded-xl px-4 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all mt-1.5"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => navigate("/auth/forgot-password")}
                  className="text-xs text-indigo-600 font-medium hover:text-indigo-700 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative mt-1.5">
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="demo123"
                  className="w-full h-12 bg-white rounded-xl px-4 pr-12 text-sm text-slate-900 placeholder:text-slate-400 border border-slate-200 focus:outline-none focus:border-indigo-300 focus:ring-2 focus:ring-indigo-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <i className={`${showPassword ? "ri-eye-off-line" : "ri-eye-line"} text-lg`}></i>
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 flex items-center gap-2">
                <i className="ri-error-warning-line text-red-500 text-sm"></i>
                <p className="text-xs text-red-600">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl text-white text-sm font-semibold shadow-lg shadow-indigo-500/25 active:scale-[0.98] transition-transform hover:from-indigo-600 hover:to-purple-700 mt-2 disabled:opacity-60 flex items-center justify-center gap-2"
            >
              {isLoading ? <><i className="ri-loader-4-line animate-spin"></i>Signing In...</> : "Sign In"}
            </button>
          </form>

        </div>

        <div className="px-5 pb-8 pt-4 max-w-[375px] mx-auto w-full">
          <p className="text-xs text-slate-400 text-center">
            Don't have an account?{" "}
            <button
              onClick={() => navigate("/auth/signup")}
              className="text-indigo-600 font-medium hover:text-indigo-700 transition-colors"
            >
              Sign Up
            </button>
          </p>
        </div>
      </div>
    </>
  );
}